// src/pages/dashboard/Referrals.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Users, DollarSign, TrendingUp, Share2, Edit2, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const getBaseUrl = () => {
  if (import.meta.env.VITE_APP_BASE_URL) {
    return import.meta.env.VITE_APP_BASE_URL;
  }
  if (typeof window !== 'undefined' && window.location.origin && window.location.origin !== 'null' && window.location.origin !== 'undefined') {
    return window.location.origin;
  }
  return 'https://gigship.com';
};
const APP_BASE_URL = getBaseUrl();

const maskEmail = (email) => {
  if (!email) return '';
  const [name, domain] = email.split('@');
  if (!domain) return email;
  if (name.length <= 2) return `${name[0] || ''}***@${domain}`;
  return `${name[0]}***${name[name.length-1]}@${domain}`;
};

const Referrals = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [shortUrl, setShortUrl] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [newReferralCode, setNewReferralCode] = useState('');
  const [hasCustomizedCode, setHasCustomizedCode] = useState(false);
  const [isGeneratingFlyer, setIsGeneratingFlyer] = useState(false);

  useEffect(() => {
    if (user) {
      loadReferralData();
    }
  }, [user]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      if (!user) {
        setLoading(false);
        return;
      }

      // Parallel fetch for stats and the shortlink viral engine
      const [statsRes, linkRes] = await Promise.all([
        api.get('/referrals/stats').catch(e => e.response?.data),
        api.post('/referrals/generate-link').catch(e => e.response?.data)
      ]);

      if (statsRes?.success) {
        setStats(statsRes.data);
        const defaultCode = user.username?.toUpperCase();
        setHasCustomizedCode(statsRes.data.referralCode !== defaultCode);
      } else {
        throw new Error('Stats fetch failed');
      }

      if (linkRes?.success) {
        setShortUrl(linkRes.data.shortUrl);
        setQrCode(linkRes.data.qrCodeBase64);
      }
      
    } catch (error) {
      console.error('Referral load error:', error);
      const defaultCode = user?.username?.toUpperCase() || 'USER';
      const defaultStats = {
        referralCode: defaultCode,
        referralLink: `${APP_BASE_URL}/register?ref=${defaultCode}`,
        totalReferrals: 0,
        activeReferrals: 0,
        referralEarnings: 0,
        referrals: []
      };
      setStats(defaultStats);
      setHasCustomizedCode(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomizeCode = async () => {
    if (!newReferralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    if (newReferralCode.length < 3 || newReferralCode.length > 15) {
      toast.error('Referral code must be between 3 and 15 characters');
      return;
    }
    if (!/^[A-Z0-9]+$/i.test(newReferralCode)) {
      toast.error('Referral code can only contain letters and numbers');
      return;
    }
    try {
      const response = await api.patch('/referrals/customize-code', {
        referralCode: newReferralCode.toUpperCase()
      });
      if (response.success) {
        toast.success('Referral code updated successfully!');
        setHasCustomizedCode(true);
        setIsEditingCode(false);
        setNewReferralCode('');
        loadReferralData(); // Reload to generate new shortlink mapped to this code
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update referral code';
      toast.error(errorMessage);
    }
  };

  const copyToClipboard = text => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = () => {
    const linkToShare = shortUrl || stats?.referralLink;
    if (!linkToShare) return;
    
    if (navigator.share) {
      navigator.share({
        title: t('referrals.share_title', 'Join GigShip'),
        text: t('referrals.share_text', {
          defaultValue: 'Turn your skills into cash on GigShip. Join with my code: {{code}}',
          code: stats?.referralCode
        }),
        url: linkToShare
      }).catch(err => console.log('Share failed:', err));
    } else {
      copyToClipboard(linkToShare);
    }
  };

  const downloadFlyer = async () => {
    const qrCanvasEl = document.getElementById('referral-qr-canvas');
    if (!qrCode && !qrCanvasEl) {
      toast.error('QR code not available');
      return;
    }
    setIsGeneratingFlyer(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');

      // 1. Background Gradient (Deep Navy Blue)
      const gradient = ctx.createLinearGradient(0, 0, 1080, 1920);
      gradient.addColorStop(0, '#0f172a'); // Very dark blue
      gradient.addColorStop(1, '#1e3a8a'); // Lighter navy blue
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1080, 1920);

      // 2. Orange Accents
      ctx.fillStyle = '#f97316'; // GigShip Orange
      ctx.beginPath();
      ctx.arc(1080, 0, 400, 0, 2 * Math.PI);
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(0, 1920, 600, 0, 2 * Math.PI);
      ctx.fill();

      // Ensure background covers the bottom slightly with a gradient mix to blend the orange
      const bottomGrad = ctx.createLinearGradient(0, 1320, 0, 1920);
      bottomGrad.addColorStop(0, 'transparent');
      bottomGrad.addColorStop(1, 'rgba(15, 23, 42, 0.9)');
      ctx.fillStyle = bottomGrad;
      ctx.fillRect(0, 1320, 1080, 600);

      // 3. Marketing Copy
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 72px "Lexend", sans-serif'; // Use brand font Lexend
      ctx.textAlign = 'center';
      
      // Welcome bonuses are suspended (founder decision 2026-10-06): the flyer promises referral sign-up only.
      // New keys, so locales that still translate the old welcome-bonus subheadlines fall back to English.
      const copy1 = t('referrals.flyer.headline');
      const copy2 = t('referrals.flyer.join_line1', { defaultValue: 'Scan to join GigShip' });
      const copy3 = t('referrals.flyer.join_line2', { defaultValue: 'with my referral code.' });
      
      ctx.fillText(copy1, 540, 400);
      ctx.font = '60px "Lexend", sans-serif';
      ctx.fillStyle = '#cbd5e1';
      ctx.fillText(copy2, 540, 500);
      ctx.fillText(copy3, 540, 580);

      // 4. Custom Round Rect drawing polyfill
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = 15;
      
      const drawRoundRect = (x, y, w, h, r) => {
        if (w < 2 * r) r = w / 2;
        if (h < 2 * r) r = h / 2;
        ctx.beginPath();
        ctx.moveTo(x+r, y);
        ctx.arcTo(x+w, y,   x+w, y+h, r);
        ctx.arcTo(x+w, y+h, x,   y+h, r);
        ctx.arcTo(x,   y+h, x,   y,   r);
        ctx.arcTo(x,   y,   x+w, y,   r);
        ctx.closePath();
      };
      
      drawRoundRect(240, 800, 600, 600, 40);
      ctx.fill();
      ctx.shadowColor = 'transparent';

      // Load and Draw QR Code
      if (qrCanvasEl) {
        ctx.drawImage(qrCanvasEl, 260, 820, 560, 560);
      } else {
        const qrImage = new Image();
        qrImage.crossOrigin = 'anonymous';
        qrImage.src = qrCode;
        await new Promise((resolve) => { qrImage.onload = resolve; qrImage.onerror = resolve; });
        ctx.drawImage(qrImage, 260, 820, 560, 560);
      }

      // 5. Shortlink Text (display-only short format, QR data is unchanged)
      ctx.textAlign = 'center';
      ctx.font = 'bold 45px monospace';
      ctx.fillStyle = '#f97316'; // Orange
      const flyerDisplayUrl = `gigship.com/r/${stats?.referralCode || ''}`;
      ctx.fillText(flyerDisplayUrl, 540, 1500);

      // 6. Referral Code
      ctx.font = 'bold 50px "Lexend", sans-serif';
      ctx.fillStyle = '#ffffff'; 
      ctx.fillText(`${t('referrals.flyer.code_prefix')} ${stats?.referralCode || ''}`, 540, 1600);

      // 7. Draw Logo at top left (optional)
      const logoImage = new Image();
      logoImage.crossOrigin = 'anonymous';
      // Use footer logo variant which is optimised for dark/navy backgrounds
      logoImage.src = '/gigship-logo-footer.png';
      await new Promise((resolve) => { logoImage.onload = resolve; logoImage.onerror = resolve; });
      if (logoImage.width > 0) {
        const ratio = 80 / logoImage.height;
        ctx.drawImage(logoImage, 80, 80, logoImage.width * ratio, 80);
      } else {
        ctx.font = 'bold 40px "Lexend", sans-serif';
        ctx.textAlign = 'left';
        ctx.fillStyle = '#ffffff';
        ctx.fillText('GigShip', 80, 120);
      }

      // Download Sequence (High-Res PNG)
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `GigShip_Flyer_${stats?.referralCode || 'QR'}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Flyer downloaded successfully!');
    } catch(err) {
      console.error('Canvas error:', err);
      toast.error('Failed to generate flyer.');
    } finally {
      setIsGeneratingFlyer(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Filter recent 5 activity
  const recentReferrals = (stats?.referrals || []).slice(0, 5);

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('referrals.referral_program')}</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 mb-1">{t('dashboard.referrals.total_referrals', 'Total Referrals')}</h3>
            <p className="text-2xl font-bold text-gray-900">{stats?.totalReferrals || 0}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 mb-1">{t('dashboard.referrals.active_referrals', 'Active Referrals')}</h3>
            <p className="text-2xl font-bold text-gray-900">{stats?.activeReferrals || 0}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 mb-1">{t('dashboard.referrals.referral_earnings', 'Referral Earnings')}</h3>
            <p className="text-2xl font-bold text-gray-900">
              {t('dashboard.referrals.gh', 'GH₵')}{stats?.referralEarnings?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>

        {/* Viral Sharing Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          
          {/* Main Referral Display */}
          <div className="lg:col-span-2 bg-gradient-to-br from-blue-900 to-indigo-900 rounded-xl shadow-xl p-8 text-white relative overflow-hidden">
            {/* Background Accent */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-orange-500 opacity-20 blur-2xl pointer-events-none"></div>

            <h2 className="text-2xl font-bold mb-4 z-10 relative">{t('referrals.your_referral_link')}</h2>
            <p className="mb-6 text-blue-100 z-10 relative">
              {t('referrals.referral_page_hero_desc', "Share full platform commission with friends you refer with your link/code. Earn up to 50% of the commission on their first order. You and the referred friend both earn from your referral.")}
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4 border border-white/20 z-10 relative">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex-1 w-full">
                  <p className="text-xs text-blue-200 uppercase tracking-wider mb-1">Your Short Link</p>
                  <code className="text-lg font-mono break-all text-white font-semibold">
                    {shortUrl || stats?.referralLink || `${APP_BASE_URL}/register?ref=${user?.username?.toUpperCase()}`}
                  </code>
                </div>
                <button 
                  onClick={() => copyToClipboard(shortUrl || stats?.referralLink)} 
                  className="p-3 bg-orange-500 rounded-lg hover:bg-orange-600 text-white font-medium transition shadow-lg w-full sm:w-auto"
                  disabled={!stats?.referralLink && !shortUrl}
                >
                  {copied ? <Check className="w-5 h-5 mx-auto" /> : <Copy className="w-5 h-5 mx-auto" />}
                </button>
              </div>
            </div>

            <div className="flex space-x-3 mb-6 z-10 relative">
              <button 
                onClick={shareReferral} 
                className="flex-1 px-4 py-3 bg-white text-blue-900 rounded-lg font-bold hover:bg-blue-50 flex items-center justify-center disabled:opacity-50 transition shadow-lg" 
                disabled={!stats?.referralLink && !shortUrl}
              >
                <Share2 className="w-5 h-5 mr-2" />
                {t('dashboard.referrals.share', 'Share Now')}
              </button>
            </div>

            {/* Referral Code Section with Edit Option */}
            <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10 z-10 relative">
              {isEditingCode ? (
                <div className="space-y-3">
                  <p className="text-sm text-blue-100 mb-2">
                    {hasCustomizedCode ? 'You can no longer change your referral code' : 'Customize your referral code (one-time only)'}
                  </p>
                  {!hasCustomizedCode && (
                    <>
                      <input 
                        type="text" 
                        value={newReferralCode} 
                        onChange={e => setNewReferralCode(e.target.value.toUpperCase())} 
                        placeholder={t('referrals.enter_custom_code')} 
                        maxLength={15} 
                        className="w-full px-4 py-2 rounded-lg text-gray-900 font-mono focus:ring-2 focus:ring-orange-500" 
                      />
                      <div className="flex gap-2">
                        <button onClick={handleCustomizeCode} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600">
                          {t('dashboard.referrals.save_code', 'Save')}
                        </button>
                        <button onClick={() => { setIsEditingCode(false); setNewReferralCode(''); }} className="flex-1 px-4 py-2 bg-white/20 rounded-lg font-medium hover:bg-white/30">
                          {t('dashboard.referrals.cancel', 'Cancel')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-200 uppercase tracking-wider mb-1">Your Promo Code</p>
                    <span className="font-bold text-white text-2xl tracking-widest">{stats?.referralCode || user?.username?.toUpperCase() || 'N/A'}</span>
                  </div>
                  {!hasCustomizedCode && (
                    <button onClick={() => setIsEditingCode(true)} className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition">
                      <Edit2 className="w-4 h-4" />
                      <span className="text-sm">{t('referrals.customize')}</span>
                    </button>
                  )}
                </div>
              )}
              {hasCustomizedCode && (
                <p className="text-xs text-blue-200 mt-2 opacity-80">
                  {t('dashboard.referrals.you_ve_customized_your_referral_code_thi', '✓ You have customized your referral code. This cannot be changed again.')}
                </p>
              )}
            </div>
          </div>

          {/* QR Code and Flyer Download */}
          <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-6 flex flex-col items-center justify-center relative overflow-hidden">
             <div className="text-center mb-4">
                <h3 className="font-bold text-gray-900 text-lg">{t('referrals.qr_title')}</h3>
                <p className="text-xs text-gray-500">{t('referrals.qr_subtitle')}</p>
             </div>
             
             {(shortUrl || stats?.referralLink) ? (
                <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-100 mb-6 w-48 h-48 flex items-center justify-center">
                  <QRCodeCanvas 
                    id="referral-qr-canvas"
                    value={shortUrl || stats?.referralLink} 
                    size={176} 
                    level={"H"}
                    includeMargin={false}
                  />
                </div>
             ) : (
                <div className="w-48 h-48 bg-gray-50 rounded-xl mb-6 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
                   <div className="animate-pulse flex space-x-2">
                     <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                     <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                     <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                   </div>
                   <span className="text-xs mt-2">{t('referrals.generating')}</span>
                </div>
             )}

             <button
               onClick={downloadFlyer}
               disabled={!(shortUrl || stats?.referralLink || qrCode) || isGeneratingFlyer}
               className="w-full py-3 bg-gray-900 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center"
             >
               {isGeneratingFlyer ? (
                 <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
               ) : (
                 <>
                   <Download className="w-4 h-4 mr-2" />
                   {t('referrals.download_flyer')}
                 </>
               )}
             </button>
          </div>
        </div>

        {/* Informational Cards */}
        <div className="bg-white rounded-lg shadow p-6 mb-8 border border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('referrals.how_it_works_title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <h4 className="font-semibold mb-2">{t('referrals.step1_title')}</h4>
              <p className="text-sm text-gray-600">{t('referrals.step1_desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-indigo-600 font-bold">2</span>
              </div>
              <h4 className="font-semibold mb-2">{t('referrals.step2_title')}</h4>
              <p className="text-sm text-gray-600">{t('referrals.step2_desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-green-600 font-bold">3</span>
              </div>
              <h4 className="font-semibold mb-2">{t('referrals.step3_title')}</h4>
              <p className="text-sm text-gray-600">{t('referrals.step3_desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-orange-600 font-bold">4</span>
              </div>
              <h4 className="font-semibold mb-2">{t('referrals.step4_title')}</h4>
              <p className="text-sm text-gray-600">{t('referrals.step4_desc')}</p>
            </div>
          </div>
        </div>

        {/* Referral List — Split into Pending and Completed */}
        {(() => {
          const allReferrals = stats?.referrals || [];
          const pendingReferrals = allReferrals.filter(r => r.status?.toLowerCase() !== 'completed');
          const completedReferrals = allReferrals.filter(r => r.status?.toLowerCase() === 'completed');

          return (
            <div className="space-y-6 mb-8">
              {/* Pending Section */}
              <div className="bg-white rounded-lg shadow border border-yellow-100">
                <div className="p-5 border-b flex justify-between items-center bg-yellow-50 rounded-t-lg">
                  <div>
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-200 text-yellow-800 text-xs font-bold">{pendingReferrals.length}</span>
                      {t('referrals.pending_section_title', 'Pending - Awaiting First Order')}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{t('referrals.pending_section_desc', 'These users signed up with your code but haven\'t completed their first order yet. Commission is unlocked once they do.')}</p>
                  </div>
                </div>
                <div className="divide-y">
                  {pendingReferrals.length === 0 ? (
                    <div className="p-6 text-center text-gray-400">
                      <p className="text-sm">{t('referrals.no_pending', 'No pending referrals. Share your link to grow your network!')}</p>
                    </div>
                  ) : (
                    pendingReferrals.map((referral, index) => (
                      <div key={referral.id || referral._id || index} className="p-4 hover:bg-gray-50 transition">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <img
                              src={referral.user?.avatar || referral.user?.profilePicture || `https://ui-avatars.com/api/?name=${referral.user?.username || 'User'}&background=random`}
                              alt={referral.user?.username}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div>
                              <p className="font-semibold text-gray-900">{referral.user?.username || 'Unknown User'}</p>
                              {referral.user?.email && <p className="text-xs text-gray-600">{maskEmail(referral.user.email)}</p>}
                              <p className="text-xs text-gray-500">Joined {new Date(referral.joinedAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-yellow-100 text-yellow-700 border border-yellow-200">
                            ⏳ {t('referrals.pending_badge', 'Awaiting First Order')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Completed Section */}
              <div className="bg-white rounded-lg shadow border border-green-100">
                <div className="p-5 border-b flex justify-between items-center bg-green-50 rounded-t-lg">
                  <div>
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-200 text-green-800 text-xs font-bold">{completedReferrals.length}</span>
                      {t('referrals.completed_section_title', 'Completed - Commission Paid')}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{t('referrals.completed_section_desc', 'These users have completed their first order. Your commission has been credited to your wallet.')}</p>
                  </div>
                </div>
                <div className="divide-y">
                  {completedReferrals.length === 0 ? (
                    <div className="p-6 text-center text-gray-400">
                      <Users className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                      <p className="text-sm">{t('referrals.no_completed', 'No completed referrals yet.')}</p>
                    </div>
                  ) : (
                    completedReferrals.map((referral, index) => (
                      <div key={referral.id || referral._id || index} className="p-4 hover:bg-gray-50 transition">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <img
                              src={referral.user?.avatar || referral.user?.profilePicture || `https://ui-avatars.com/api/?name=${referral.user?.username || 'User'}&background=random`}
                              alt={referral.user?.username}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div>
                              <p className="font-semibold text-gray-900">{referral.user?.username || 'Unknown User'}</p>
                              {referral.user?.email && <p className="text-xs text-gray-600">{maskEmail(referral.user.email)}</p>}
                              <p className="text-xs text-gray-500">Joined {new Date(referral.joinedAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-green-100 text-green-700 border border-green-200">
                            ✓ {t('referrals.completed_badge', 'Commission Paid')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Referrals;